import {
  Accordion,
  Alert,
  Badge,
  Group,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { graphql, type ResultOf } from "gql.tada";
import { useQuery } from "@apollo/client/react";
import { toIsoTimestamp } from "./validation.service";

const VALIDATION_ASSERTION_EVENTS_QUERY = graphql(`
  query ValidationAssertionEvents($jobId: ID!) {
    stepRuleSetValidationEvents(
      jobId: $jobId
      kindFilter: [ASSERTION]
      page: { first: 100 }
    ) {
      nodes {
        id
        kind
        summary
        ruleId
        sequence
        timestamp
      }
    }
  }
`);

const VALIDATION_JOB_RESULT_QUERY = graphql(`
  query ValidationJobResultForChecklist($jobId: ID!) {
    stepRuleSetValidationJob(jobId: $jobId) {
      id
      status
      errorSummary
      result {
        success
        invariantResults {
          code
          severity
          passed
          message
        }
      }
    }
  }
`);

interface AssertionEvent {
  readonly id: string;
  readonly kind: string;
  readonly summary: string;
  readonly ruleId: string | null | undefined;
  readonly sequence: number;
  readonly timestamp: string;
}

interface InvariantRow {
  readonly key: string;
  readonly label: string;
  readonly passed: boolean;
  readonly severity: string;
  readonly detail: string | null;
}

interface ValidationInvariantChecklistProps {
  readonly jobId: string;
}

/**
 * Fallback keyword heuristic used to classify assertion summaries as failing
 * ONLY when the job has not yet materialised a structured `result.invariantResults`
 * payload (i.e. the job is still running). Once the job terminates, we prefer
 * the backend's explicit `passed: Boolean` flag.
 */
const FAILURE_KEYWORDS: readonly string[] = [
  "fail",
  "violat",
  "never",
  "error",
  "missing",
  "invalid",
];

function isFailedAssertion(summary: string): boolean {
  const lower = summary.toLowerCase();
  return FAILURE_KEYWORDS.some((kw) => lower.includes(kw));
}

function ValidationInvariantChecklist(
  props: ValidationInvariantChecklistProps,
) {
  const { data: jobData, loading: jobLoading } = useQuery(
    VALIDATION_JOB_RESULT_QUERY,
    { variables: { jobId: props.jobId } },
  );

  // Only query streaming ASSERTION events when the structured result is not
  // yet available (job still running, or backend cannot produce a result).
  const job = jobData?.stepRuleSetValidationJob;
  const structuredResults = job?.result?.invariantResults ?? null;
  const hasStructuredResult =
    structuredResults !== null && structuredResults.length > 0;

  const { data: eventsData, loading: eventsLoading } = useQuery(
    VALIDATION_ASSERTION_EVENTS_QUERY,
    {
      variables: { jobId: props.jobId },
      skip: hasStructuredResult,
    },
  );

  const loading = jobLoading && !jobData;
  const eventsLoadingBlocked =
    !hasStructuredResult && eventsLoading && !eventsData;

  if (loading || eventsLoadingBlocked) {
    return (
      <Stack gap="xs">
        <Skeleton height={16} width={200} />
        <Skeleton height={40} radius="sm" />
        <Skeleton height={40} radius="sm" />
      </Stack>
    );
  }

  const rows: readonly InvariantRow[] = hasStructuredResult
    ? structuredResults.map<InvariantRow>((row, idx) => ({
        key: `${row.code}-${idx}`,
        label: row.code,
        passed: row.passed,
        severity: row.severity,
        detail: row.message ?? null,
      }))
    : mapEventsToRows(eventsData?.stepRuleSetValidationEvents?.nodes ?? []);

  if (rows.length === 0) return null;

  const passedCount = rows.reduce((n, r) => (r.passed ? n + 1 : n), 0);

  return (
    <Stack gap="xs">
      <Text fw={600}>
        Invariant checklist ({passedCount}/{rows.length} passing)
      </Text>
      {job?.errorSummary ? (
        <Alert color="red" mb="xs">
          {job.errorSummary}
        </Alert>
      ) : null}
      <Accordion multiple variant="separated">
        {rows.map((row) => {
          const failed = !row.passed;
          return (
            <Accordion.Item key={row.key} value={row.key}>
              <Accordion.Control
                icon={
                  failed ? (
                    <IconX color="red" size={18} />
                  ) : (
                    <IconCheck color="green" size={18} />
                  )
                }
              >
                <Group justify="space-between" wrap="nowrap">
                  <Text
                    size="sm"
                    c={failed ? "red" : "dimmed"}
                    style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
                  >
                    {row.label}
                    {row.detail ? ` — ${row.detail}` : ""}
                  </Text>
                  <Group gap={6} wrap="nowrap">
                    <Badge
                      color={row.severity === "WARNING" ? "yellow" : "gray"}
                      variant="outline"
                      size="xs"
                    >
                      {row.severity}
                    </Badge>
                    <Badge color={failed ? "red" : "green"} variant="light">
                      {failed ? "FAIL" : "PASS"}
                    </Badge>
                  </Group>
                </Group>
              </Accordion.Control>
              {failed && row.detail ? (
                <Accordion.Panel>
                  <Text size="xs" c="dimmed">
                    {row.detail}
                  </Text>
                </Accordion.Panel>
              ) : null}
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Stack>
  );
}

// The gql.tada DateTime scalar is typed as `unknown` by default — we use the
// existing `toIsoTimestamp` narrowing helper here so this is a pure shape
// match, not a new unknown usage in business logic.
type AssertionEventPayload = ResultOf<
  typeof VALIDATION_ASSERTION_EVENTS_QUERY
>["stepRuleSetValidationEvents"]["nodes"][number];

function mapEventsToRows(
  events: readonly AssertionEventPayload[],
): readonly InvariantRow[] {
  const mapped: AssertionEvent[] = events.map((event) => ({
    id: event.id,
    kind: "ASSERTION",
    summary: event.summary,
    ruleId: event.ruleId,
    sequence: event.sequence,
    timestamp: toIsoTimestamp(event.timestamp),
  }));
  return mapped.map<InvariantRow>((event) => ({
    key: event.id,
    label: event.summary,
    passed: !isFailedAssertion(event.summary),
    severity: "ERROR",
    detail: event.ruleId ? `Rule ID: ${event.ruleId}` : null,
  }));
}

export {
  ValidationInvariantChecklist,
  VALIDATION_ASSERTION_EVENTS_QUERY,
  VALIDATION_JOB_RESULT_QUERY,
};
